import { createLogger } from '../utils/logger';
import { addBreadcrumb, updateSentryUserContext } from '../config/sentry';
import type { CheckoutData } from '../types';
import type { PaymentData } from '../pages/PaymentStatusPage';
import type { Checkout } from '../types/checkout';

const logger = createLogger('CheckoutService');

// Configurações do serviço de checkout
const CHECKOUT_CONFIG = {
  baseURL: import.meta.env.VITE_CHECKOUT_BASE_URL || 'http://localhost:54321',
  timeout: 60000, // 1 minuto para processamento completo
};

// Funções auxiliares para cálculo de parcelas
function calculateTotalValue(
  baseValue: number,
  installments: number,
  feePercentage: number
): number {
  if (installments <= 1) {
    return baseValue;
  }

  // Aplicar taxa apenas a partir da 2ª parcela
  const monthlyRate = feePercentage / 100;
  const totalWithFees = baseValue * Math.pow(1 + monthlyRate, installments - 1);

  return Number(totalWithFees.toFixed(2));
}

function calculateInstallmentValue(
  baseValue: number,
  installments: number,
  feePercentage: number
): number {
  const totalValue = calculateTotalValue(
    baseValue,
    installments,
    feePercentage
  );
  const installmentValue = totalValue / installments;

  return Number(installmentValue.toFixed(2));
}

function calculatePixDiscountValue(
  baseValue: number,
  discountPercentage?: number | null
): number {
  if (!discountPercentage || discountPercentage <= 0) {
    return baseValue;
  }

  const discountAmount = (baseValue * discountPercentage) / 100;
  const finalValue = baseValue - discountAmount;

  return Number(Math.max(finalValue, 0).toFixed(2));
}

export interface CheckoutRequest {
  // Dados pessoais do cliente
  id?: string;
  customer?: {
    address: string;
    addressNumber: string;
    city: string;
    complement?: string;
    cpfCnpj: string;
    email: string;
    name: string;
    phone: string;
    postalCode: string;
    province: string;
    state: string;
  };
  payment: {
    method: 'CREDIT_CARD' | 'PIX' | 'BOLETO';
    installmentCount?: number;
    installmentValue?: number;
    totalValue?: number;
    value?: number;
    dueDate?: string;
    includePixQrCode?: boolean;
    creditCard?: {
      creditCardHolderInfo?: {
        addressNumber: string;
        cpfCnpj: string;
        email: string;
        name: string;
        phone: string;
        postalCode: string;
      };
      ccv: string;
      expiryMonth: string;
      expiryYear: string;
      holderName: string;
      number: string;
    };
  };
  callback?: {
    successUrl?: string;
    autoRedirect?: boolean;
  };
}

export interface CheckoutResponse {
  success: boolean;
  data?: PaymentData;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export class CheckoutApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CheckoutApiError';
  }
}

class CheckoutService {
  private async makeRequest<T>(
    endpoint: string,
    data: CheckoutRequest
  ): Promise<T> {
    const url = `${CHECKOUT_CONFIG.baseURL}${endpoint}`;

    // Adicionar breadcrumb para rastreamento
    addBreadcrumb(`Iniciando requisição para ${endpoint}`, 'api', 'info', {
      url,
      method: 'POST',
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : [],
    });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        CHECKOUT_CONFIG.timeout
      );

      logger.info(`Fazendo requisição para ${endpoint}`, {
        url,
        timeout: CHECKOUT_CONFIG.timeout,
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0`,
          'X-Multimoney-Token': '698253e3-f61c-486f-820b-69cbb215e74a',
        },
        body: JSON.stringify(data),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      addBreadcrumb(`Resposta recebida de ${endpoint}`, 'api', 'info', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      });

      const responseData = await response.json();

      if (!response.ok) {
        const apiError = this.handleApiError(response, responseData, endpoint);

        // Log estruturado do erro de API
        logger.exception(apiError, `Erro na API ${endpoint}`, {
          status: response.status,
          statusText: response.statusText,
          responseData,
          endpoint,
          url,
        });

        // Adicionar contexto do erro ao usuário no Sentry
        updateSentryUserContext({
          lastApiError: {
            endpoint,
            status: response.status,
            statusText: response.statusText,
            timestamp: new Date().toISOString(),
            errorMessage: apiError.message,
          },
        });

        throw apiError;
      }

      logger.info(`Requisição ${endpoint} concluída com sucesso`);
      return responseData;
    } catch (error) {
      if (error instanceof CheckoutApiError) {
        throw error;
      }

      let apiError: CheckoutApiError;

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          apiError = new CheckoutApiError(
            'Timeout no processamento do checkout'
          );
          logger.exception(apiError, `Timeout na requisição ${endpoint}`, {
            timeout: CHECKOUT_CONFIG.timeout,
            endpoint,
            url,
          });
        } else {
          apiError = new CheckoutApiError(
            `Erro na requisição: ${error.message}`
          );
          logger.exception(error, `Erro de rede na requisição ${endpoint}`, {
            endpoint,
            url,
            originalError: error.message,
            stack: error.stack,
          });
        }
      } else {
        apiError = new CheckoutApiError('Erro desconhecido no checkout');
        logger.exception(error, `Erro desconhecido na requisição ${endpoint}`, {
          endpoint,
          url,
          error,
        });
      }

      throw apiError;
    }
  }

  private handleApiError(
    response: Response,
    responseData: unknown,
    _endpoint: string
  ): CheckoutApiError {
    // Type guard para validar o formato da resposta
    const isResponseDataWithError = (
      data: unknown
    ): data is { error: unknown } => {
      return typeof data === 'object' && data !== null && 'error' in data;
    };

    if (isResponseDataWithError(responseData)) {
      if (
        typeof responseData.error === 'string' &&
        responseData.error.includes('errors')
      ) {
        try {
          const parsedError = JSON.parse(responseData.error);
          const errorMessage = parsedError?.errors[0]?.description;

          if (errorMessage) {
            return new CheckoutApiError(errorMessage);
          }
        } catch (parseError) {
          logger.warn('Erro ao parsear resposta de erro da API', {
            originalError: responseData.error,
            parseError:
              parseError instanceof Error ? parseError.message : parseError,
          });
        }
      } else if (typeof responseData.error === 'string') {
        return new CheckoutApiError(responseData.error);
      } else if (
        typeof responseData.error === 'object' &&
        responseData.error !== null &&
        'message' in responseData.error &&
        typeof (responseData.error as { message: unknown }).message === 'string'
      ) {
        return new CheckoutApiError(
          (responseData.error as { message: string }).message
        );
      }
    }

    return new CheckoutApiError(
      `Erro HTTP ${response.status}: ${response.statusText}`
    );
  }

  async getCheckout(
    paymentId: string,
    includePixQrCode: boolean
  ): Promise<{
    success: boolean;
    data?: PaymentData;
    error?: {
      message: string;
    };
  }> {
    const requestData = {
      id: paymentId,
      includePixQrCode,
    };

    return this.makeRequest<{
      success: boolean;
      data?: PaymentData;
      error?: {
        message: string;
      };
    }>('/functions/v1/checkout-get', requestData as unknown as CheckoutRequest);
  }

  async getCheckoutById(checkoutId: string): Promise<{
    success: boolean;
    data?: Checkout;
    error?: {
      message: string;
    };
  }> {
    try {
      const url = `${CHECKOUT_CONFIG.baseURL}/functions/v1/checkout-data`;

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        CHECKOUT_CONFIG.timeout
      );

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0`,
          'X-Multimoney-Token': '698253e3-f61c-486f-820b-69cbb215e74a',
        },
        body: JSON.stringify({ id: checkoutId } satisfies Record<
          string,
          string
        >),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            message:
              responseData.error?.message ||
              `Erro HTTP ${response.status}: ${response.statusText}`,
          },
        };
      }

      return {
        success: true,
        data: responseData.data,
      };
    } catch (error) {
      logger.exception(error, 'Erro ao buscar checkout por ID', {
        checkoutId,
        url: `${CHECKOUT_CONFIG.baseURL}/functions/v1/checkout-data`,
        timeout: CHECKOUT_CONFIG.timeout,
      });

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: { message: 'Timeout ao buscar checkout' },
          };
        }
        return {
          success: false,
          error: { message: `Erro na requisição: ${error.message}` },
        };
      }

      return {
        success: false,
        error: { message: 'Erro desconhecido ao buscar checkout' },
      };
    }
  }

  async processCheckout(checkoutData: CheckoutData): Promise<CheckoutResponse> {
    // Calcular valores de desconto

    console.log('checkoutData', checkoutData);

    // Estruturar dados para envio
    const requestData: CheckoutRequest = {
      id: checkoutData.checkoutId,
      customer: {
        address: checkoutData.personal.address.street,
        addressNumber: checkoutData.personal.address.number,
        city: checkoutData.personal.address.city,
        complement: checkoutData.personal.address.complement,
        cpfCnpj: checkoutData.personal.document,
        email: checkoutData.personal.email,
        name: `${checkoutData.personal.firstName} ${checkoutData.personal.lastName}`,
        phone: checkoutData.personal.phone,
        postalCode: checkoutData.personal.address.zipCode,
        province: checkoutData.personal.address.neighborhood,
        state: checkoutData.personal.address.state,
      },
      payment: {
        method: checkoutData.payment.method,
        installmentCount:
          checkoutData.payment.method === 'CREDIT_CARD' ||
          checkoutData.payment.method === 'BOLETO'
            ? checkoutData.payment.installments || 1
            : 1,
        installmentValue: (() => {
          if (checkoutData.payment.method === 'CREDIT_CARD') {
            return calculateInstallmentValue(
              checkoutData.checkout.amount || 0,
              checkoutData.payment.installments || 1,
              checkoutData.checkout.credit_card_installment_fee_percentage ||
                checkoutData.checkout.installment_fee_percentage ||
                0
            );
          } else if (checkoutData.payment.method === 'BOLETO') {
            return calculateInstallmentValue(
              checkoutData.checkout.amount || 0,
              checkoutData.payment.installments || 1,
              checkoutData.checkout.boleto_installment_fee_percentage || 0
            );
          }
          return 0;
        })(),
        totalValue: (() => {
          if (checkoutData.payment.method === 'CREDIT_CARD') {
            return calculateTotalValue(
              checkoutData.checkout.amount || 0,
              checkoutData.payment.installments || 1,
              checkoutData.checkout.credit_card_installment_fee_percentage ||
                checkoutData.checkout.installment_fee_percentage ||
                0
            );
          } else if (checkoutData.payment.method === 'BOLETO') {
            return calculateTotalValue(
              checkoutData.checkout.amount || 0,
              checkoutData.payment.installments || 1,
              checkoutData.checkout.boleto_installment_fee_percentage || 0
            );
          }
          return checkoutData.checkout.amount || 0;
        })(),
        value:
          checkoutData.payment.method === 'PIX'
            ? calculatePixDiscountValue(
                checkoutData.checkout.amount || 0,
                checkoutData.checkout.pix_discount_percentage
              )
            : checkoutData.checkout.amount || 0,
        dueDate:
          checkoutData.payment.method === 'BOLETO' &&
          checkoutData.checkout.due_days
            ? new Date(
                Date.now() +
                  checkoutData.checkout.due_days * 24 * 60 * 60 * 1000
              )
                .toISOString()
                .split('T')[0]
            : new Date().toISOString().split('T')[0], // Data atual para cartão e PIX
        includePixQrCode: true,
        creditCard: {
          creditCardHolderInfo: {
            addressNumber: checkoutData.personal.address.number,
            cpfCnpj: checkoutData.personal.document,
            email: checkoutData.personal.email,
            name: `${checkoutData.personal.firstName} ${checkoutData.personal.lastName}`,
            phone: checkoutData.personal.phone,
            postalCode: checkoutData.personal.address.zipCode,
          },
          ccv: checkoutData.payment.creditCard?.cvv || '',
          expiryMonth:
            checkoutData.payment.creditCard?.expiryDate.split('/')[0] || '',
          expiryYear:
            checkoutData.payment.creditCard?.expiryDate.split('/')[1] || '',
          holderName: `${checkoutData.personal.firstName} ${checkoutData.personal.lastName}`,
          number: checkoutData.payment.creditCard?.number || '',
        },
      },
      callback: {
        successUrl:
          checkoutData.checkout.success_url ||
          import.meta.env.VITE_APP_URL +
            '/success?payment=' +
            checkoutData.checkoutId,
        autoRedirect: true,
      },
    };

    console.log('requestData', requestData);

    return this.makeRequest<CheckoutResponse>(
      '/functions/v1/checkout-create',
      requestData
    );
  }
}

// Singleton instance
export const checkoutService = new CheckoutService();
export { CheckoutService };
